import React, { useMemo } from "react";
import Legend from "./legand";
import "./graphs.css";
import { COLORS, fmt } from "./utils";

export interface TissueSoilRow {
    field_guid: string;
    field_name: string;
    nutrient_name: string;
    tissue_min_value: number;
    tissue_max_value: number;
    tissue_avg_value: number;
    percent_of_field: number;
    severity: string;
    tissue_very_low: number;
    tissue_low: number;
    tissue_medium: number;
    tissue_high: number;
    soil_min_value: number;
    soil_max_value: number;
    soil_avg_value: number;
    soil_very_low: number;
    soil_low: number;
    soil_medium: number;
    soil_high: number;
    customer_name: string;
}

interface Field {
    name: string;
    soilData: TissueSoilRow[];
}

function groupByField(data: TissueSoilRow[]): Field[] {
    const map = new Map<string, Field>();
    for (const row of data) {
        if (!map.has(row.field_guid)) {
            map.set(row.field_guid, { name: row.field_name, soilData: [] });
        }
        map.get(row.field_guid)!.soilData.push(row);
    }

    const result: Field[] = [];
    for (const field of [...map.values()].sort((a, b) => a.name.localeCompare(b.name))) {
        if (field.soilData.length <= 4) {
            result.push(field);
        } else {
            for (let i = 0; i < field.soilData.length; i += 4) {
                const chunk = field.soilData.slice(i, i + 4);
                const name = i === 0 ? field.name : `${field.name} (cont.)`;
                result.push({ name, soilData: chunk });
            }
        }
    }
    return result;
}

function Bar({ high, medium, low, veryLow, avg, label }) {
    const soilDisplayTop = Math.max(high * 1.5, avg * 1.1);
    const soilBands = [
        { from: 0, to: veryLow, color: COLORS[0] },
        { from: veryLow, to: low, color: COLORS[1] },
        { from: low, to: medium, color: COLORS[2] },
        { from: medium, to: high, color: COLORS[3] },
        { from: high, to: soilDisplayTop, color: COLORS[4] },
    ];
    const toPercent = (v: number) => 11 + (1 - Math.min(v / high, 1)) * 89;
    const avgTop = toPercent(avg);
    const mainBands = soilBands.slice(0, 4);

    return (
        <div className="barSection">
            <div className="barContainer">
                <div style={{ height: "11%", background: COLORS[4] }} className="barPart">
                    <span>{fmt(high)}</span>
                </div>

                {mainBands.map((b, i) => {
                    const top = toPercent(b.to);
                    const bottom = toPercent(b.from);
                    const height = bottom - top;
                    if (height <= 0) return null;
                    return (
                        <div
                            key={i}
                            style={{
                                top: `${top}%`,
                                height: `${height}%`,
                                background: b.color,
                            }}
                            className="barPart"
                        >
                            <span>{b.from !== 0 && fmt(b.from)}</span>
                        </div>
                    );
                })}

                <div className="avgBlock" style={{ top: `${avgTop}%` }}>
                    <span>{fmt(avg)}</span>
                </div>
            </div>
            <span className="typeLabel">{label}</span>
        </div>
    );
}

function NutrientBar({ soilData }: { soilData: TissueSoilRow }) {
    const tissueBarData = {
        high: soilData.tissue_high,
        medium: soilData.tissue_medium,
        low: soilData.tissue_low,
        veryLow: soilData.tissue_very_low,
        avg: soilData.tissue_avg_value,
        label: "Tissue",
    };

    const soilBarData = {
        high: soilData.soil_high,
        medium: soilData.soil_medium,
        low: soilData.soil_low,
        veryLow: soilData.soil_very_low,
        avg: soilData.soil_avg_value,
        label: "Soil",
    };

    return (
        <div className="barCoupleContainer">
            <div className="barCouple">
                <Bar {...tissueBarData} />
                <Bar {...soilBarData} />
            </div>
            <div className="nutrient">{soilData.nutrient_name}</div>
        </div>
    );
}

function FieldSection({ field }: { field: Field }) {
    return (
        <div className="fieldSection">
            <div className="fieldName">{field.name}</div>
            <div className="barGroup">
                {field.soilData.map((n) => (
                    <NutrientBar key={n.nutrient_name} soilData={n} />
                ))}
            </div>
        </div>
    );
}

interface NutrientChartProps {
    data: TissueSoilRow[];
    legendData: string[][];
}

export default function SoilTissueChart({ data, legendData }: NutrientChartProps) {
    const fields = useMemo(() => groupByField(data), [data]);

    return (
        <div className="analysisResultBarSection">
            {legendData === undefined ? (
                <></>
            ) : (
                <Legend legendData={legendData} legandLabel={["Tissue", "Soil"]}></Legend>
            )}
            {fields.map((field) => (
                <FieldSection key={field.name} field={field} />
            ))}
        </div>
    );
}
